import { DEFAULT_APP_LANGUAGE, enabledLanguages } from "@app/i18n"
import {
	clearNavigateToPathHandler,
	setNavigateToPathHandler,
} from "@shared/api/client/interceptors/status-check/navigate"
import {
	getLanguageFromLocale,
	resolveToSupportedLanguage,
} from "@shared/i18n/utils"
import { useAuth } from "@shared/lib/auth"
import { useUserSettingsStore } from "@shared/stores"
import { LoadingPage } from "@shared/ui"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { createBrowserRouter, RouterProvider } from "react-router-dom"

import { carbonBankRoutes } from "./routes"

const CarbonBankRouter = () => {
	const { i18n } = useTranslation()

	const [currentLanguage, updateLanguage] = useUserSettingsStore(state => [
		state.currentLanguage,
		state.updateLanguage,
	])

	const {
		isAuthenticated,
		userData,
		startLoginFlow,
		fetchUserPermissions,
		fetchUserProfile,
	} = useAuth()

	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		void startLoginFlow()
	}, [])

	useEffect(() => {
		if (!isAuthenticated) {
			setIsLoading(false)
			return
		}

		Promise.all([fetchUserPermissions(), fetchUserProfile()]).finally(() =>
			setIsLoading(false),
		)
	}, [isAuthenticated])

	useEffect(() => {
		const setInitialLanguage = () => {
			if (userData?.defaultLocale) {
				const supportedLanguage = enabledLanguages.find(
					sl =>
						getLanguageFromLocale(userData.defaultLocale || "") ===
						getLanguageFromLocale(sl),
				)
				updateLanguage(supportedLanguage || DEFAULT_APP_LANGUAGE)

				return
			}

			const resolvedLanguage = resolveToSupportedLanguage(
				currentLanguage || navigator.language,
				enabledLanguages,
			)
			updateLanguage(resolvedLanguage || DEFAULT_APP_LANGUAGE)
		}

		const changeLanguage = () => {
			if (currentLanguage) {
				i18n.changeLanguage(currentLanguage)
			}
		}

		setInitialLanguage()
		changeLanguage()
	}, [userData, currentLanguage, i18n, updateLanguage])

	const router = useMemo(() => createBrowserRouter(carbonBankRoutes), [])

	useEffect(() => {
		setNavigateToPathHandler(path => router.navigate(path))

		return () => {
			clearNavigateToPathHandler()
		}
	}, [router])

	return (
		<>{isLoading ? <LoadingPage /> : <RouterProvider router={router} />}</>
	)
}

export default CarbonBankRouter
